<template>
  <div class="lp-page auth-page">
    <div class="auth-split">
      <!-- Form side -->
      <div class="auth-form-side">
        <div class="auth-card">
          <div class="auth-logo">
            <img src="/img/logo_text.png" alt="Invoice Scanner" />
          </div>

          <div class="auth-header">
            <h1 class="auth-title">Create Account</h1>
            <p class="auth-subtitle">Sign up to start scanning invoices</p>
          </div>

          <div v-if="error" class="alert alert-error">
            <LineIcon name="alert-circle" :size="15" :stroke-width="1.8" />
            <span>{{ error }}</span>
          </div>

          <a :href="googleLoginUrl" class="btn-google">
            <svg class="google-icon" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </a>

          <div class="divider"><span>or</span></div>

          <form @submit.prevent="handleRegister" class="auth-form">
            <div class="form-group">
              <label for="name" class="form-label">Full Name</label>
              <input id="name" v-model="form.name" type="text" required class="form-input" placeholder="John Doe" :disabled="loading" />
            </div>
            <div class="form-group">
              <label for="email" class="form-label">Email</label>
              <input id="email" v-model="form.email" type="email" required class="form-input" placeholder="you@example.com" :disabled="loading" />
            </div>
            <div class="form-group">
              <label for="password" class="form-label">Password</label>
              <div class="password-input-wrapper">
                <input id="password" v-model="form.password" :type="showPassword ? 'text' : 'password'" required minlength="8" class="form-input" placeholder="At least 8 characters" :disabled="loading" />
                <button type="button" @click="showPassword = !showPassword" class="password-toggle" tabindex="-1">
                  <LineIcon :name="showPassword ? 'eye-off' : 'eye'" :size="15" :stroke-width="1.8" />
                </button>
              </div>
              <p class="form-hint">Must be at least 8 characters long</p>
            </div>
            <div class="form-group">
              <label for="password_confirmation" class="form-label">Confirm Password</label>
              <div class="password-input-wrapper">
                <input id="password_confirmation" v-model="form.password_confirmation" :type="showPasswordConfirm ? 'text' : 'password'" required class="form-input" placeholder="Re-enter your password" :disabled="loading" />
                <button type="button" @click="showPasswordConfirm = !showPasswordConfirm" class="password-toggle" tabindex="-1">
                  <LineIcon :name="showPasswordConfirm ? 'eye-off' : 'eye'" :size="15" :stroke-width="1.8" />
                </button>
              </div>
            </div>
            <div class="form-group">
              <label class="checkbox-label terms-label">
                <input v-model="form.terms" type="checkbox" class="checkbox-input" required />
                <span>I agree to the <a href="/terms" target="_blank" class="inline-link">Terms of Service</a> and <a href="/privacy-policy.html" target="_blank" class="inline-link">Privacy Policy</a></span>
              </label>
            </div>
            <button type="submit" class="btn-submit" :disabled="loading">
              <span v-if="loading"><font-awesome-icon icon="fa-solid fa-spinner" spin /> Creating account...</span>
              <span v-else>Create Account</span>
            </button>
          </form>

          <div class="auth-footer">
            <p>Already have an account? <router-link to="/login" class="auth-link">Sign in</router-link></p>
          </div>
        </div>
      </div>

      <!-- Brand side -->
      <aside class="auth-brand">
        <div class="brand-glow"></div>
        <div class="brand-content">
          <div class="brand-logo">
            <span class="brand-logo-tile"><img src="/img/logo_quare.svg" alt="" /></span>
            <span class="brand-name">Invoice Scan</span>
          </div>
          <h2 class="brand-title">Start automating<br>in minutes</h2>
          <p class="brand-sub">Create your account and turn every invoice into clean, exportable data — automatically.</p>
          <ul class="brand-points">
            <li><span class="bp-tick"><LineIcon name="check" :size="15" :stroke-width="1.8" /></span> 10 free scans every month</li>
            <li><span class="bp-tick"><LineIcon name="check" :size="15" :stroke-width="1.8" /></span> AI extraction with 99.5% accuracy</li>
            <li><span class="bp-tick"><LineIcon name="check" :size="15" :stroke-width="1.8" /></span> Export to any accounting system</li>
          </ul>
          <div class="brand-trust">Trusted by 100+ businesses</div>
        </div>
      </aside>
    </div>
  </div>
</template>

<script setup>
import LineIcon from '@/components/icons/LineIcon.vue';
import { ref, computed } from 'vue';
import { useRouter } from 'vue-router';
import axios from 'axios';

const router = useRouter();

const form = ref({ name: '', email: '', password: '', password_confirmation: '', terms: false });
const loading = ref(false);
const error = ref('');
const showPassword = ref(false);
const showPasswordConfirm = ref(false);

const googleLoginUrl = computed(() => '/auth/google');

const handleRegister = async () => {
  error.value = '';
  if (form.value.password !== form.value.password_confirmation) {
    error.value = 'Passwords do not match';
    return;
  }
  loading.value = true;
  try {
    const response = await axios.post('/api/auth/register', {
      name: form.value.name,
      email: form.value.email,
      password: form.value.password,
      password_confirmation: form.value.password_confirmation
    });
    if (response.data.success) {
      window.location.href = '/';
    }
  } catch (err) {
    if (err.response?.data?.message) {
      error.value = err.response.data.message;
    } else if (err.response?.data?.errors) {
      const errors = err.response.data.errors;
      error.value = Object.values(errors).flat().join(', ');
    } else {
      error.value = 'An error occurred. Please try again.';
    }
  } finally {
    loading.value = false;
  }
};
</script>

<style scoped>
.auth-page { min-height: 100vh; background: var(--bg); }
.auth-split { display: grid; grid-template-columns: 1fr 1fr; min-height: 100vh; }

/* form side */
.auth-form-side { display: flex; align-items: center; justify-content: center; padding: 40px 24px; }
.auth-card { width: 100%; max-width: 420px; background: transparent; }

.auth-logo { display: flex; justify-content: center; margin-bottom: 24px; }
.auth-logo img { height: 38px; }
.auth-header { text-align: center; margin-bottom: 26px; }
.auth-title { font-size: 30px; font-weight: 700; letter-spacing: -0.03em; color: var(--ink); margin: 0 0 8px; }
.auth-subtitle { font-size: 15px; color: var(--muted); margin: 0; }

.alert { display: flex; align-items: center; gap: 12px; padding: 12px 16px; border-radius: 12px; margin-bottom: 20px; font-size: 14px; font-weight: 500; }
.alert-error { background: #fef2f2; color: #dc2626; border: 1px solid #fecaca; }
.alert svg { flex-shrink: 0; }

.btn-google { display: flex; align-items: center; justify-content: center; gap: 12px; width: 100%; padding: 14px 24px; background: #fff; border: 1px solid var(--line); border-radius: var(--pill); font-size: 15px; font-weight: 600; color: var(--ink); text-decoration: none; transition: all 0.2s ease; cursor: pointer; }
.btn-google:hover { border-color: #cbd5e1; transform: translateY(-2px); box-shadow: 0 14px 30px -18px rgba(31,41,51,0.35); }
.google-icon { width: 20px; height: 20px; }

.divider { display: flex; align-items: center; margin: 20px 0; color: var(--muted); font-size: 14px; }
.divider::before, .divider::after { content: ''; flex: 1; height: 1px; background: var(--line); }
.divider span { padding: 0 16px; }

.auth-form { display: flex; flex-direction: column; gap: 16px; }
.form-group { display: flex; flex-direction: column; gap: 7px; }
.form-label { font-size: 13.5px; font-weight: 600; color: var(--ink); }
.form-input { width: 100%; padding: 12px 16px; border: 1px solid var(--line); border-radius: 12px; font-size: 15px; color: var(--ink); background: #fbfbfd; transition: all 0.2s ease; font-family: inherit; }
.form-input:focus { outline: none; border-color: var(--violet); box-shadow: 0 0 0 3px rgba(43,130,240,0.12); background: #fff; }
.form-input:disabled { background: #f1f3f6; cursor: not-allowed; }
.form-hint { font-size: 12px; color: var(--muted); margin: 0; }
.password-input-wrapper { position: relative; }
.password-toggle { position: absolute; right: 12px; top: 50%; transform: translateY(-50%); background: none; border: none; color: var(--muted); cursor: pointer; padding: 8px; display: flex; transition: color 0.2s ease; }
.password-toggle:hover { color: var(--violet); }

.checkbox-label { display: flex; align-items: flex-start; gap: 9px; font-size: 13.5px; color: var(--muted); cursor: pointer; line-height: 1.5; }
.checkbox-input { width: 16px; height: 16px; cursor: pointer; accent-color: var(--violet); margin-top: 2px; flex-shrink: 0; }
.inline-link { color: var(--violet); text-decoration: none; font-weight: 600; }
.inline-link:hover { text-decoration: underline; }

.btn-submit { width: 100%; padding: 14px 24px; background: var(--violet); color: #fff; border: none; border-radius: var(--pill); font-size: 16px; font-weight: 600; cursor: pointer; transition: all 0.2s ease; margin-top: 4px; box-shadow: 0 16px 30px -16px rgba(43,130,240,0.6); }
.btn-submit:hover:not(:disabled) { background: var(--violet-hover); transform: translateY(-2px); }
.btn-submit:disabled { opacity: 0.6; cursor: not-allowed; transform: none; }

.auth-footer { margin-top: 20px; text-align: center; font-size: 14px; color: var(--muted); }
.auth-link { color: var(--violet); text-decoration: none; font-weight: 600; }
.auth-link:hover { text-decoration: underline; }

/* brand side */
.auth-brand { position: relative; background: var(--dark); color: #fff; overflow: hidden; display: flex; align-items: center; }
.brand-glow { position: absolute; left: 50%; top: 42%; width: 720px; height: 720px; transform: translate(-50%, -50%); background: radial-gradient(circle, rgba(43,130,240,0.32), rgba(43,130,240,0) 62%); pointer-events: none; }
.brand-content { position: relative; z-index: 1; padding: 64px 72px; max-width: 520px; }
.brand-logo { display: flex; align-items: center; gap: 12px; margin-bottom: 40px; }
.brand-logo-tile { width: 44px; height: 44px; border-radius: 12px; background: #fff; display: inline-flex; align-items: center; justify-content: center; }
.brand-logo-tile img { width: 26px; height: 26px; }
.brand-name { font-size: 18px; font-weight: 700; letter-spacing: -0.01em; }
.brand-title { color: #fff; font-size: clamp(30px, 3vw, 42px); font-weight: 600; letter-spacing: -0.035em; line-height: 1.1; margin: 0 0 16px; }
.brand-sub { font-size: 17px; color: rgba(255,255,255,0.62); line-height: 1.55; margin: 0 0 34px; max-width: 40ch; }
.brand-points { list-style: none; margin: 0 0 36px; padding: 0; display: flex; flex-direction: column; gap: 16px; }
.brand-points li { display: flex; align-items: center; gap: 14px; font-size: 15.5px; font-weight: 500; color: rgba(255,255,255,0.92); }
.bp-tick { width: 26px; height: 26px; border-radius: 50%; background: rgba(43,130,240,0.25); color: #8fc6ff; display: inline-flex; align-items: center; justify-content: center; font-size: 11px; flex-shrink: 0; }
.brand-trust { font-size: 13px; font-weight: 600; letter-spacing: 0.06em; text-transform: uppercase; color: rgba(255,255,255,0.45); }

@media (max-width: 900px) {
  .auth-split { grid-template-columns: 1fr; }
  .auth-brand { display: none; }
}
</style>
